import { createContext, useContext, useEffect, useState } from 'react';

const GoogleMapsContext = createContext(null);
const SCRIPT_ID = 'google-maps-script';

export function GoogleMapsProvider({ children }) {
    console.log('🌐 GOOGLE MAPS PROVIDER INITIALIZED', {
        timestamp: new Date().toISOString()
    });

    const [isLoaded, setIsLoaded] = useState(false);
    const [error, setError] = useState(null);
    
    // Debug environment variable
    const envKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
    console.log('🔍 ENV KEY DEBUG', {
        envKey,
        envKeyType: typeof envKey,
        envKeyTruthy: !!envKey,
        envKeyLength: envKey?.length,
        allEnvKeys: Object.keys(import.meta.env),
        timestamp: new Date().toISOString()
    });
    
    // Use hardcoded key since env var is not available
    const apiKey = "AIzaSyAPxdnagP6kNVgqoleAJ6PIslx6qP2SOZ4";

    console.log('🔑 API KEY CHECK', {
        hasApiKey: !!apiKey,
        apiKeyLength: apiKey?.length || 0,
        timestamp: new Date().toISOString()
    });

    useEffect(() => {
        console.log('🚀 GOOGLE MAPS SCRIPT LOADER EFFECT', {
            hasApiKey: !!apiKey,
            hasGoogleMaps: !!window.google?.maps,
            timestamp: new Date().toISOString()
        });

        if (!apiKey || apiKey.trim() === '') {
            console.error('❌ NO API KEY', {
                error: 'Google Maps API key is not configured',
                apiKey,
                timestamp: new Date().toISOString()
            });
            setError('Google Maps API key is not configured');
            return;
        }

        // Check if script is already loaded
        if (window.google?.maps) {
            console.log('✅ GOOGLE MAPS ALREADY LOADED', {
                hasGoogle: !!window.google,
                hasMaps: !!window.google?.maps,
                timestamp: new Date().toISOString()
            });
            setIsLoaded(true);
            return;
        }

        // Check if script is already being loaded
        const existingScript = document.getElementById(SCRIPT_ID);
        if (existingScript) {
            console.log('🔍 FOUND EXISTING SCRIPT', {
                scriptId: SCRIPT_ID,
                src: existingScript.src,
                timestamp: new Date().toISOString()
            });
            existingScript.addEventListener('load', () => {
                console.log('✅ EXISTING SCRIPT LOADED', { timestamp: new Date().toISOString() });
                setIsLoaded(true);
            });
            existingScript.addEventListener('error', () => {
                console.error('❌ EXISTING SCRIPT ERROR', { timestamp: new Date().toISOString() });
                setError('Failed to load Google Maps');
            });
            return;
        }

        // Create new script if none exists
        console.log('🆕 CREATING NEW GOOGLE MAPS SCRIPT', {
            scriptId: SCRIPT_ID,
            libraries: ['places'],
            timestamp: new Date().toISOString()
        });

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;

        script.addEventListener('load', () => {
            console.log('✅ GOOGLE MAPS SCRIPT LOADED', {
                hasGoogle: !!window.google,
                hasMaps: !!window.google?.maps,
                timestamp: new Date().toISOString()
            });
            setIsLoaded(true);
        });

        script.addEventListener('error', (error) => {
            console.error('❌ GOOGLE MAPS SCRIPT ERROR', {
                error,
                message: error?.message,
                timestamp: new Date().toISOString()
            });
            setError('Failed to load Google Maps');
        });

        document.head.appendChild(script);
        console.log('📎 SCRIPT APPENDED TO HEAD', { timestamp: new Date().toISOString() });

        return () => {
            // Don't remove the script - let it persist for the lifetime of the app
            // This prevents the "multiple initialization" warning
        };
    }, [apiKey]);

    return (
        <GoogleMapsContext.Provider value={{ isLoaded, error }}>
            {children}
        </GoogleMapsContext.Provider>
    );
}

export function useGoogleMaps() {
    const context = useContext(GoogleMapsContext);
    if (!context) {
        console.error('❌ useGoogleMaps ERROR: No context found', {
            timestamp: new Date().toISOString()
        });
        throw new Error('useGoogleMaps must be used within a GoogleMapsProvider');
    }
    console.log('🎯 useGoogleMaps HOOK CALLED', {
        isLoaded: context.isLoaded,
        error: context.error,
        timestamp: new Date().toISOString()
    });
    return context;
}
